import { goalsApi, metricsApi, reflectionsApi } from "@/api";
import { readDashboardCache, writeDashboardCache } from "@/lib/dashboard-cache";
import { readDashboardControls } from "@/lib/dashboard-controls";

/**
 * Start fetching the dashboard the moment someone presses its row, not when
 * the screen mounts.
 *
 * `/dashboard` is a route: the screen is built from nothing on arrival and
 * three requests begin then. `onPressIn` fires a few hundred milliseconds
 * earlier — press, release, navigation animation — so the work overlaps the
 * part of the interaction the user is already waiting through.
 *
 * Prefetching on INTENT rather than on arrival at Profile is the whole design.
 * Profile is a high-frequency settings surface (edit, notifications, plan,
 * language, sign out) and the dashboard is one optional row on it, so firing
 * the app's heaviest read on every Profile visit would spend database work on
 * the many people who never open it. Pressing the row is as close to certain
 * as intent gets.
 *
 * Bounded on purpose, and every one of these matters:
 *
 * - **Single-flight.** A second press while one is on the wire joins it
 *   instead of doubling the burst. Press-in fires on every touch, including
 *   the ones that become scrolls.
 * - **Never a duplicate of the screen itself.** The screen takes the in-flight
 *   promise with `takeDashboardPrefetch()` and awaits it rather than starting
 *   its own identical requests, so a press that lands before this finishes
 *   costs one set of requests, not two.
 * - **Nothing to gain, nothing spent.** An already-cached dashboard for this
 *   user, range and reset marker skips outright.
 * - **Never on the cold-start path**, by construction: nothing calls this
 *   except a finger on the row.
 *
 * Failure is silent. This is speculative work the user did not ask for; the
 * screen makes the same requests on arrival and reports honestly if they fail.
 */

let inFlight: Promise<void> | null = null;

/** Only ever the Week view: the screen resets to it on every open. */
const PREFETCH_RANGE = 7 as const;

export function prefetchDashboard(userId: string | null | undefined): void {
  if (!userId) return;
  if (inFlight) return;

  const run = async () => {
    const controls = await readDashboardControls();
    // Nothing to gain: this user, range and reset marker are already in
    // memory, and the screen will paint from that same entry.
    if (readDashboardCache(userId, PREFETCH_RANGE, controls.resetAt)) return;

    const [progress, goals, quota] = await Promise.all([
      metricsApi.progress(PREFETCH_RANGE, controls.resetAt),
      goalsApi.list(),
      reflectionsApi.quota(),
    ]);
    writeDashboardCache(userId, PREFETCH_RANGE, controls.resetAt, {
      progress,
      goals,
      quota,
    });
  };

  const promise = run()
    .catch(() => {
      // Speculative: the screen asks again on arrival and owns the error.
    })
    .finally(() => {
      if (inFlight === promise) inFlight = null;
    });
  inFlight = promise;
}

/**
 * The prefetch currently on the wire, handed to the screen so it can await it
 * instead of racing it.
 *
 * Cleared as it is handed over: exactly one caller may treat a prefetch as
 * "the load I would otherwise have started", and that is the screen the user
 * just opened.
 */
export function takeDashboardPrefetch(): Promise<void> | null {
  const pending = inFlight;
  inFlight = null;
  return pending;
}

/** Test seam: forget any in-flight prefetch. */
export function resetDashboardPrefetch(): void {
  inFlight = null;
}
